import {
  chartMeasureIds,
  findMeasure,
  formatMeasure,
  pilotMeasures,
  type PilotMeasure,
} from './kaiCoachContent';

import styles from './KaiCoachResultsChart.module.css';

const GRID_STEPS = [0, 25, 50, 75, 100] as const;
const chartMeasures = chartMeasureIds.map(findMeasure);

/** "43% in 2025, 73% in 2026": what the row says without the picture. */
export function describeChange(measure: PilotMeasure): string {
  return `${formatMeasure(measure, measure.before)} in 2025, ${formatMeasure(measure, measure.after)} in 2026`;
}

/** The decorative track: a 0–100% scale drawn with SVG percentages. */
function Track({ measure }: { measure: PilotMeasure }) {
  const low = Math.min(measure.before, measure.after);
  const high = Math.max(measure.before, measure.after);

  return (
    <svg aria-hidden="true" className={styles.track} focusable="false">
      {GRID_STEPS.map((step) => (
        <line className={styles.grid} key={step} x1={`${step}%`} x2={`${step}%`} y1="0" y2="100%" />
      ))}
      <line className={styles.connector} x1={`${low}%`} x2={`${high}%`} y1="50%" y2="50%" />
      <circle className={styles.before} cx={`${measure.before}%`} cy="50%" r="7" />
      <circle className={styles.after} cx={`${measure.after}%`} cy="50%" r="7" />
    </svg>
  );
}

/** 2025 against 2026 for each percentage measure, with every value in text. */
export function KaiCoachResultsChart() {
  return (
    <figure aria-labelledby="kai-coach-chart-title" className={styles.card}>
      <div className={styles.head}>
        <div>
          <h3 className={styles.title} id="kai-coach-chart-title">
            Share of student work, 2025 vs 2026
          </h3>
          <p className={styles.subtitle}>
            Each row: the 2025 cohort (no coach) and the 2026 cohort (Kai Coach)
          </p>
        </div>
        <ul aria-label="Legend" className={styles.legend}>
          <li className={styles.legendItem}>
            <span aria-hidden="true" className={`${styles.swatch} ${styles.swatchBefore}`} />
            2025 · no coach
          </li>
          <li className={styles.legendItem}>
            <span aria-hidden="true" className={`${styles.swatch} ${styles.swatchAfter}`} />
            2026 · Kai Coach
          </li>
        </ul>
      </div>

      <div aria-hidden="true" className={styles.axis}>
        <span />
        <span className={styles.ticks}>
          {GRID_STEPS.map((step) => (
            <span key={step}>{step}%</span>
          ))}
        </span>
        <span />
      </div>
      <ul className={styles.rows}>
        {chartMeasures.map((measure) => (
          <li className={styles.row} key={measure.id}>
            <span className={styles.label}>
              {measure.chart?.label}
              <small className={styles.context}>{measure.chart?.context}</small>
            </span>
            <Track measure={measure} />
            <span className={styles.values}>
              <span aria-hidden="true">
                <span className={styles.from}>{formatMeasure(measure, measure.before)}</span>
                {' → '}
                <strong className={styles.to}>{formatMeasure(measure, measure.after)}</strong>
              </span>
              <span className={styles.srOnly}>{describeChange(measure)}</span>
            </span>
          </li>
        ))}
      </ul>

      <details className={styles.details}>
        <summary className={styles.summary}>See every measure as a table</summary>
        <table className={styles.table}>
          <caption className={styles.caption}>
            Measured from submitted text. A developed argument paragraph makes a claim, supplies
            evidence and interprets that evidence.
          </caption>
          <thead>
            <tr>
              <th scope="col">Measure</th>
              <th scope="col">2025</th>
              <th scope="col">2026</th>
            </tr>
          </thead>
          <tbody>
            {pilotMeasures.map((measure) => (
              <tr key={measure.id}>
                <th scope="row">{measure.label}</th>
                <td>{formatMeasure(measure, measure.before)}</td>
                <td>{formatMeasure(measure, measure.after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
